import { isNil } from 'es-toolkit';
import { isEmpty } from 'es-toolkit/compat';

import { appRoutes, RouterEntitiesId } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';

import { CompanyType, PeoInfoFragment, PeoStaffRole, PeoWhere, UserStatus } from 'src/api';

import { buildUrl } from './router';

export type PeosTableFilter = {
  status?: Nullable<UserStatus[]>;
};

const getPeoAdmins = (
  peoStaffs: PeoInfoFragment['peoStaffsList'],
): PeoInfoFragment['peoStaffsList'] => {
  return peoStaffs.filter(staffUser => staffUser.role === PeoStaffRole.PeoAdmin);
};

const determinePeoAdminsStatus = (peoAdmins: PeoInfoFragment['peoStaffsList']): UserStatus => {
  if (isEmpty(peoAdmins)) {
    return UserStatus.Inactive;
  }

  if (peoAdmins.some(peoAdmin => peoAdmin.user.status === UserStatus.Active)) {
    return UserStatus.Active;
  }

  if (peoAdmins.every(peoAdmin => peoAdmin.user.status === UserStatus.Pending)) {
    return UserStatus.Pending;
  }

  return UserStatus.Inactive;
};

export const getPeoStatus = (peo: PeoInfoFragment): UserStatus => {
  const peoAdmins = getPeoAdmins(peo.peoStaffsList);
  return determinePeoAdminsStatus(peoAdmins);
};

export const preparePeosFilterByStatus = (filter: PeosTableFilter): PeoWhere | undefined => {
  if (isNil(filter.status) || isEmpty(filter.status)) return undefined;

  const hasActiveAdmin = {
    peoStaffs: {
      some: {
        role: { equals: PeoStaffRole.PeoAdmin },
        user: { status: { equals: UserStatus.Active } },
      },
    },
  } satisfies PeoWhere;

  const hasPendingAdmin = {
    peoStaffs: {
      some: {
        role: { equals: PeoStaffRole.PeoAdmin },
        user: { status: { equals: UserStatus.Pending } },
      },
    },
  } satisfies PeoWhere;

  const hasInactiveAdmin = {
    peoStaffs: {
      some: {
        role: { equals: PeoStaffRole.PeoAdmin },
        user: { status: { equals: UserStatus.Inactive } },
      },
    },
  } satisfies PeoWhere;

  const hasAnyAdmin = {
    peoStaffs: {
      some: {
        role: { equals: PeoStaffRole.PeoAdmin },
      },
    },
  } satisfies PeoWhere;

  return {
    OR: filter.status.map(status => {
      // Active: at least one Admin with Active status
      if (status === UserStatus.Active) {
        return hasActiveAdmin;
      }

      // Pending: no Active admins, all admins are Pending (at least one exists)
      if (status === UserStatus.Pending) {
        return {
          AND: [{ NOT: [hasActiveAdmin] }, hasPendingAdmin, { NOT: [hasInactiveAdmin] }],
        };
      }

      // Inactive: no Active admins AND (no admins at all OR at least one Inactive admin)
      return {
        AND: [
          { NOT: [hasActiveAdmin] },
          {
            OR: [{ NOT: [hasAnyAdmin] }, hasInactiveAdmin],
          },
        ],
      };
    }),
  };
};

export const buildPeoDetailsUrl = (peoId: string) => {
  return buildUrl(appRoutes.peos.detail.companyInformation, {
    [RouterEntitiesId.peo]: peoId,
  });
};

export const buildPartnershipDetailsUrl = (companyId: string, companyType: CompanyType) => {
  if (companyType === CompanyType.Peo) {
    return buildUrl(appRoutes.partnerships.detail.peo.companyInformation, {
      [RouterEntitiesId.peo]: companyId,
    });
  }

  if (companyType === CompanyType.PeoPayrollProvider) {
    return buildUrl(appRoutes.partnerships.detail.peoPayrollProvider.companyInformation, {
      [RouterEntitiesId.peoPayrollProvider]: companyId,
    });
  }

  throw new Error('Invalid company type for partnership details URL');
};
